import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import { 
  signIn as amplifySignIn, 
  signOut as amplifySignOut, 
  signUp as amplifySignUp, 
  confirmSignUp as amplifyConfirmSignUp, 
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession
} from '@aws-amplify/auth';
import { UserProfile } from '../types';

// Context type
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, attributes: any) => Promise<any>;
  confirmSignUp: (email: string, code: string) => Promise<any>;
  forgotPassword: (email: string) => Promise<any>;
  forgotPasswordSubmit: (email: string, code: string, newPassword: string) => Promise<any>;
  refreshAuthState: () => Promise<void>;
}

// Constants
const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // State now directly reflects current auth status without persistence
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);

  // Function to refresh the auth state by querying Amplify
  const refreshAuthState = useCallback(async () => {
    setIsLoading(true);
    try {
      // For Amplify auth
      try {
        const currentUser = await getCurrentUser();
        
        if (currentUser && currentUser.userId) {
          try {
            // Fetch the user attributes separately - this is the proper Amplify v6 way
            const userAttributes = await fetchUserAttributes();
            
            // Create a user profile from the currentUser and fetched attributes
            const userProfile: UserProfile = {
              userId: currentUser.userId,
              email: userAttributes.email || '',
              firstName: userAttributes.given_name || '',
              lastName: userAttributes.family_name || '',
              role: userAttributes['custom:role'] || '',
              groups: []
            };
            
            setIsAuthenticated(true);
            setUser(userProfile);
          } catch (attributesError) {
            console.error("Error fetching user attributes:", attributesError);
            // Still authenticate the user even if we can't get attributes
            // but with minimal profile information
            const minimalProfile: UserProfile = {
              userId: currentUser.userId,
              email: '',
              firstName: '',
              lastName: '',
              role: '',
              groups: []
            };
            
            setIsAuthenticated(true);
            setUser(minimalProfile);
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (amplifyError) {
        // Expected error for unauthenticated users
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      // No authenticated user
      console.error("Authentication error:", error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Initialize auth state on mount
  useEffect(() => {
    refreshAuthState();
  }, [refreshAuthState]);

  // Periodically refresh tokens if authenticated
  useEffect(() => {
    // Only set up refresh if authenticated
    if (!isAuthenticated) return;
    
    // Set up interval for token refresh
    const intervalId = setInterval(() => {
      refreshAuthState();
    }, TOKEN_REFRESH_INTERVAL);
    
    // Clean up interval on unmount or when auth state changes
    return () => clearInterval(intervalId);
  }, [isAuthenticated, refreshAuthState]);

  // Sign in implementation
  const signIn = async (email: string, password: string) => {
    try {
      // Pre-emptive sign-out to clear any existing auth state
      try {
        await signOut();
      } catch (signOutError) {
        // If sign-out fails, just continue
        console.warn("Pre-emptive sign-out failed:", signOutError);
      }
      
      // Sign in with Amplify
      let signInResult;
      try {
        signInResult = await amplifySignIn({ username: email, password });
      } catch (apiError) {
        console.error("Sign-in API error:", apiError);
        throw apiError;
      }
      
      // Refresh our auth state
      try {
        await refreshAuthState();
      } catch (refreshError) {
        console.error("Error refreshing auth state after sign-in:", refreshError);
        // Continue anyway since sign-in was successful
      }
      
      return signInResult;
    } catch (error) {
      console.error("Sign-in error:", error);
      throw error;
    }
  };

  // Sign out implementation
  const signOut = async () => {
    try {
      await amplifySignOut();
      
      // Update local state
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error("Sign-out error:", error);
      throw error;
    }
  };

  // Sign up implementation
  const signUp = async (email: string, password: string, attributes: Record<string, string>) => {
    try {
      return await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...attributes
          }
        }
      });
    } catch (error) {
      throw error;
    }
  };

  // Confirm sign up implementation
  const confirmSignUp = async (email: string, code: string) => {
    try {
      return await amplifyConfirmSignUp({
        username: email,
        confirmationCode: code
      });
    } catch (error) {
      throw error;
    }
  };

  // Reset password implementation
  const forgotPassword = async (email: string) => {
    try {
      return await amplifyResetPassword({ username: email });
    } catch (error) {
      throw error;
    }
  };

  // Confirm reset password implementation
  const forgotPasswordSubmit = async (email: string, code: string, newPassword: string) => {
    try {
      return await amplifyConfirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
    } catch (error) {
      throw error;
    }
  };

  // Context value
  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    signIn,
    signOut,
    signUp,
    confirmSignUp,
    forgotPassword,
    forgotPasswordSubmit,
    refreshAuthState,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};